import getDecryptedToken from "../../../../api/getDecryptedToken";
import { setEncryptedToken } from "../../../../api/encriptToken";
import {
  addMinutesToHm,
  formatTimeHm,
  minutesBetweenTimes,
} from "../components/appointmentScheduleUi";

export const CLINIC_TIME_DURATION_CHANGED_EVENT = "clinicTimeDurationChanged";

/** Default slot length and step for new appointments (minutes). */
export const DEFAULT_APPOINTMENT_TIME_GAP = 10;

/** @deprecated Use DEFAULT_APPOINTMENT_TIME_GAP or getStoredAppointmentTimeGap() */
export const NEW_APPOINTMENT_INTERVAL_MINUTES = DEFAULT_APPOINTMENT_TIME_GAP;

export const APPOINTMENT_TIME_GAP_OPTIONS = [5, 10, 15] as const;
export type AppointmentTimeGapMinutes = (typeof APPOINTMENT_TIME_GAP_OPTIONS)[number];

/** Default length of one calendar-selected appointment slot (minutes). */
export const NEW_APPOINTMENT_SLOT_MINUTES = 15;

/** Matches calendar click granularity (`CusomizeCalender` STEP). */
export const NEW_APPOINTMENT_CALENDAR_STEP_MINUTES = 5;

/**
 * HTML `type="time"` step for the end-time field.
 * Must match calendar clicks (every 5 min), not clinic time gap — otherwise
 * e.g. 10:05 + 15 min → 10:20 is invalid when gap is 15 (quarter-hour grid only).
 */
export const NEW_APPOINTMENT_TIME_INPUT_STEP_SECONDS =
  NEW_APPOINTMENT_CALENDAR_STEP_MINUTES * 60;

export function isValidAppointmentTimeGap(
  n: number
): n is AppointmentTimeGapMinutes {
  return n === 5 || n === 10 || n === 15;
}

export function parseAppointmentTimeGap(
  value: unknown
): AppointmentTimeGapMinutes {
  const n = parseInt(String(value ?? ""), 10);
  return isValidAppointmentTimeGap(n) ? n : DEFAULT_APPOINTMENT_TIME_GAP;
}

/** Reads clinic `timeDuration` saved in the encrypted login session. */
export function getStoredAppointmentTimeGap(): AppointmentTimeGapMinutes {
  return parseAppointmentTimeGap(getDecryptedToken("timeDuration"));
}

/** Updates encrypted session after clinical settings save (no re-login required). */
export function setStoredAppointmentTimeGap(minutes: number): void {
  if (!isValidAppointmentTimeGap(minutes)) return;
  setEncryptedToken("timeDuration", String(minutes));
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(CLINIC_TIME_DURATION_CHANGED_EVENT, {
        detail: minutes,
      })
    );
  }
}

export function getNewAppointmentDurationOptions(
  gapMinutes?: number
): number[] {
  const gap = isValidAppointmentTimeGap(gapMinutes ?? NaN)
    ? gapMinutes!
    : getStoredAppointmentTimeGap();
  const options = [1, 2, 3, 4, 5, 6].map((n) => n * gap);
  const set = new Set(options);
  set.add(NEW_APPOINTMENT_SLOT_MINUTES);
  return Array.from(set).sort((a, b) => a - b);
}

/** @deprecated Use getNewAppointmentDurationOptions(gap) */
export const NEW_APPOINTMENT_DURATION_OPTIONS = getNewAppointmentDurationOptions(
  DEFAULT_APPOINTMENT_TIME_GAP
);

/** Snap a duration in minutes to the nearest step (minimum one step). */
export function snapNewAppointmentDuration(
  minutes: number,
  step?: number
): number {
  const gap = isValidAppointmentTimeGap(step ?? NaN)
    ? step!
    : getStoredAppointmentTimeGap();
  if (!Number.isFinite(minutes) || minutes <= 0) return NEW_APPOINTMENT_SLOT_MINUTES;
  if (minutes === NEW_APPOINTMENT_SLOT_MINUTES) return NEW_APPOINTMENT_SLOT_MINUTES;
  return Math.max(gap, Math.round(minutes / gap) * gap);
}

export function parseQueueNumber(raw: string): number | null {
  const trimmed = String(raw ?? "").trim();
  if (trimmed === "") return 0;
  if (!/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function formatAppointmentDateYmd(value: unknown): string | null {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function collectUsedSessionQueues(
  appointments: any[],
  opts: { date: string; doctorId: string; sessionName: string }
): Set<number> {
  const used = new Set<number>();
  const sessionKey = String(opts.sessionName ?? "").trim();
  const doctorKey = String(opts.doctorId ?? "").trim();
  if (!sessionKey || !doctorKey || !opts.date) return used;

  for (const apt of appointments) {
    if (!apt) continue;
    if (String(apt.sessionName ?? "").trim() !== sessionKey) continue;
    const aptDoctor = String(apt.doctorId?._id ?? apt.doctorId ?? "").trim();
    if (aptDoctor !== doctorKey) continue;
    const aptDate = formatAppointmentDateYmd(apt.date);
    if (aptDate !== opts.date) continue;
    const q = parseQueueNumber(String(apt.queue ?? ""));
    // 0 is the default / unassigned queue — not a reserved slot in the session.
    if (q !== null && q !== 0) used.add(q);
  }
  return used;
}

export function buildNewAppointmentFormState(detail: any) {
  const startTime = formatTimeHm(detail?.startTime);
  const endTime = formatTimeHm(detail?.endTime);
  const fromSlot = minutesBetweenTimes(startTime, endTime);
  const interval =
    Number.isFinite(fromSlot) && fromSlot > 0
      ? fromSlot
      : NEW_APPOINTMENT_SLOT_MINUTES;
  const resolvedEnd =
    endTime || (startTime ? addMinutesToHm(startTime, interval) : "");

  return {
    title: "",
    fullName: "",
    gender: "",
    mobileNo: "",
    dob: "",
    address: "",
    doctorId: detail?.doctorId,
    date: detail?.date,
    startTime,
    endTime: resolvedEnd,
    interval: String(interval),
    purpose: [] as string[],
    activeTime: "",
    patientId: "",
    queue: "0",
    sessionName: detail?.sessionName,
    walkIn: Boolean(detail?.walkIn),
    callIn: Boolean(detail?.callIn),
  };
}

export type NewAppointmentFormState = ReturnType<
  typeof buildNewAppointmentFormState
>;
